import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { toast } from "sonner";
import { Trash2, Edit3, Plus, X, Check, XSquare, Mail, Send } from "lucide-react";

const EMPTY = {
  title: "", description: "", category: "travel", merchant: "", url: "",
  image_url: "", discount: "", original_price: "", sale_price: "", featured: false, expires_at: "",
  location_city: "", is_national: true,
};

const CATS = ["travel", "clothing", "auto", "stocks", "crypto", "electronics", "home", "food", "beauty", "gaming", "health"];
const CITIES = ["", "New York", "Los Angeles", "San Francisco", "Chicago", "Austin", "Miami", "Boston", "Seattle", "London", "Toronto", "Sydney", "Melbourne", "Berlin"];

export default function Admin() {
  const { user, loading: authLoading } = useAuth();
  const [deals, setDeals] = useState([]);
  const [pending, setPending] = useState([]);
  const [subs, setSubs] = useState([]);
  const [emailStatus, setEmailStatus] = useState(null);
  const [emailLog, setEmailLog] = useState([]);
  const [sendingDaily, setSendingDaily] = useState(false);
  const [sendingWeekly, setSendingWeekly] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    const [d, p, s, es, el] = await Promise.all([
      api.get("/deals"),
      api.get("/deals/pending").catch(() => ({ data: [] })),
      api.get("/newsletter/list").catch(() => ({ data: [] })),
      api.get("/newsletter/status").catch(() => ({ data: null })),
      api.get("/newsletter/email-log").catch(() => ({ data: [] })),
    ]);
    setDeals(d.data);
    setPending(p.data || []);
    setSubs(s.data || []);
    setEmailStatus(es.data);
    setEmailLog(el.data || []);
  };

  useEffect(() => { if (user?.role === "admin") load(); }, [user]);

  if (authLoading) return <div className="max-w-7xl mx-auto px-6 py-12">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    try {
      // Convert local datetime-input string ("2026-02-15T18:30") to ISO with Z; empty string -> null
      const payload = {
        ...form,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
      };
      if (editingId) {
        await api.put(`/deals/${editingId}`, payload);
        toast.success("Deal updated");
      } else {
        await api.post("/deals", payload);
        toast.success("Deal created");
      }
      setForm(EMPTY);
      setEditingId(null);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const edit = (deal) => {
    let localExp = "";
    if (deal.expires_at) {
      const d = new Date(deal.expires_at);
      // Format YYYY-MM-DDTHH:MM in local timezone for datetime-local input
      const pad = (n) => String(n).padStart(2, "0");
      localExp = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }
    setForm({
      title: deal.title, description: deal.description, category: deal.category,
      merchant: deal.merchant, url: deal.url, image_url: deal.image_url || "",
      discount: deal.discount || "", original_price: deal.original_price || "",
      sale_price: deal.sale_price || "", featured: !!deal.featured, expires_at: localExp,
      location_city: deal.location_city || "", is_national: deal.is_national !== false,
    });
    setEditingId(deal.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this deal?")) return;
    try {
      await api.delete(`/deals/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const approve = async (id) => {
    try {
      await api.post(`/deals/${id}/approve`);
      toast.success("Approved and published");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const reject = async (id) => {
    try {
      await api.post(`/deals/${id}/reject`);
      toast.success("Rejected");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const sendDaily = async () => {
    if (!window.confirm(`Send today's deal to ${emailStatus?.subscribers || 0} subscribers?`)) return;
    setSendingDaily(true);
    try {
      const { data } = await api.post("/newsletter/send-daily");
      toast.success(`Sent to ${data.sent} · Failed: ${data.failed}${data.personalized ? ` · ${data.personalized} personalized` : ""}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSendingDaily(false);
    }
  };

  const sendWeekly = async () => {
    if (!window.confirm(`Send weekly recap to ${emailStatus?.subscribers || 0} subscribers?`)) return;
    setSendingWeekly(true);
    try {
      const { data } = await api.post("/newsletter/send-weekly");
      toast.success(`Weekly recap sent to ${data.sent} · Failed: ${data.failed}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSendingWeekly(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12" data-testid="admin-page">
      <div className="flex items-baseline justify-between mb-10">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#E63946] mb-3">Admin</div>
          <h1 className="font-serif font-black text-5xl tracking-tighter">Deal manager</h1>
        </div>
        <button
          onClick={() => { setShowForm(!showForm); setEditingId(null); setForm(EMPTY); }}
          className="inline-flex items-center gap-2 bg-black text-white px-5 py-3 font-bold uppercase tracking-widest text-xs hover:bg-[#E63946]"
          data-testid="admin-new-deal"
        >
          {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />} {showForm ? "Close" : "New deal"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="border border-black p-6 md:p-8 mb-12 space-y-4 bg-white" data-testid="admin-deal-form">
          <h2 className="font-serif font-bold text-2xl">{editingId ? "Edit deal" : "Create deal"}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input required placeholder="Title" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="border border-black px-3 py-2" data-testid="admin-title" />
            <input required placeholder="Merchant" value={form.merchant} onChange={(e) => setForm({...form, merchant: e.target.value})} className="border border-black px-3 py-2" data-testid="admin-merchant" />
            <input required placeholder="URL (https://...)" value={form.url} onChange={(e) => setForm({...form, url: e.target.value})} className="border border-black px-3 py-2 md:col-span-2" data-testid="admin-url" />
            <input placeholder="Image URL" value={form.image_url} onChange={(e) => setForm({...form, image_url: e.target.value})} className="border border-black px-3 py-2 md:col-span-2" data-testid="admin-image" />
            <select value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} className="border border-black px-3 py-2" data-testid="admin-category">
              {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input placeholder="Discount (e.g. 40% OFF)" value={form.discount} onChange={(e) => setForm({...form, discount: e.target.value})} className="border border-black px-3 py-2" data-testid="admin-discount" />
            <input placeholder="Original price" value={form.original_price} onChange={(e) => setForm({...form, original_price: e.target.value})} className="border border-black px-3 py-2" />
            <input placeholder="Sale price" value={form.sale_price} onChange={(e) => setForm({...form, sale_price: e.target.value})} className="border border-black px-3 py-2" />
            <div className="md:col-span-2">
              <label className="text-xs font-bold uppercase tracking-widest block mb-1">Expires at (optional)</label>
              <input
                type="datetime-local"
                value={form.expires_at}
                onChange={(e) => setForm({...form, expires_at: e.target.value})}
                className="border border-black px-3 py-2 w-full font-mono"
                data-testid="admin-expires-at"
              />
              <p className="text-xs text-black/50 mt-1">Countdown shows on cards. Deals stay visible for 24h after expiring, then are hidden.</p>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest block mb-1">City (optional)</label>
              <select
                value={form.location_city}
                onChange={(e) => setForm({...form, location_city: e.target.value, is_national: e.target.value === "" ? form.is_national : false})}
                className="border border-black px-3 py-2 w-full"
                data-testid="admin-location-city"
              >
                {CITIES.map((c) => <option key={c || "national"} value={c}>{c || "— National —"}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_national} onChange={(e) => setForm({...form, is_national: e.target.checked})} data-testid="admin-is-national" />
              National (shows everywhere)
            </label>
            <textarea required placeholder="Description" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="border border-black px-3 py-2 md:col-span-2 min-h-[80px]" data-testid="admin-description" />
            <label className="flex items-center gap-2 md:col-span-2 text-sm">
              <input type="checkbox" checked={form.featured} onChange={(e) => setForm({...form, featured: e.target.checked})} data-testid="admin-featured" />
              Featured on homepage
            </label>
          </div>
          <button type="submit" className="bg-black text-white px-6 py-3 font-bold uppercase tracking-widest text-xs hover:bg-[#E63946]" data-testid="admin-submit">
            {editingId ? "Update deal" : "Create deal"}
          </button>
        </form>
      )}

      {/* Pending submissions queue */}
      <div className="mb-16" data-testid="pending-section">
        <div className="flex items-baseline gap-3 mb-4">
          <h2 className="font-serif font-bold text-2xl">Pending submissions</h2>
          {pending.length > 0 && (
            <span className="bg-[#E63946] text-white text-xs font-bold uppercase tracking-widest px-2 py-1" data-testid="pending-count">
              {pending.length} awaiting
            </span>
          )}
        </div>
        {pending.length === 0 ? (
          <div className="border border-dashed border-black/20 p-8 text-center text-sm text-black/50" data-testid="no-pending">
            Inbox zero. No community submissions waiting.
          </div>
        ) : (
          <div className="border border-black/10 bg-white divide-y divide-black/10">
            {pending.map((d) => (
              <div key={d.id} className="p-4 flex items-start gap-4" data-testid={`pending-row-${d.id}`}>
                <div className="w-20 h-20 bg-black/5 overflow-hidden shrink-0">
                  {d.image_url && <img src={d.image_url} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-black/40 mb-1">
                    <span>{d.category}</span>
                    <span>·</span>
                    <span>{d.merchant}</span>
                    {d.discount && <><span>·</span><span className="text-[#E63946] font-bold">{d.discount}</span></>}
                  </div>
                  <div className="font-serif font-bold text-lg leading-tight">{d.title}</div>
                  <p className="text-sm text-black/60 mt-1 line-clamp-2">{d.description}</p>
                  <div className="flex items-center gap-3 text-xs text-black/50 mt-2">
                    <span>By <strong>{d.submitted_by_name || "user"}</strong></span>
                    <a href={d.url} target="_blank" rel="noopener noreferrer" className="underline hover:text-[#E63946] truncate max-w-xs">{d.url}</a>
                  </div>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <button
                    onClick={() => approve(d.id)}
                    className="inline-flex items-center gap-1.5 bg-black text-white px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-[#E63946]"
                    data-testid={`approve-${d.id}`}
                  >
                    <Check className="w-3.5 h-3.5" strokeWidth={2} /> Approve
                  </button>
                  <button
                    onClick={() => reject(d.id)}
                    className="inline-flex items-center gap-1.5 border border-black px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-black hover:text-white"
                    data-testid={`reject-${d.id}`}
                  >
                    <XSquare className="w-3.5 h-3.5" strokeWidth={2} /> Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Deals table */}
      <div className="mb-16">
        <h2 className="font-serif font-bold text-2xl mb-4">Deals ({deals.length})</h2>
        <div className="border border-black/10 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-black text-white text-xs uppercase tracking-widest">
              <tr>
                <th className="text-left px-4 py-3">Title</th>
                <th className="text-left px-4 py-3">Category</th>
                <th className="text-left px-4 py-3">Merchant</th>
                <th className="text-left px-4 py-3">Discount</th>
                <th className="text-left px-4 py-3">Featured</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {deals.map((d) => (
                <tr key={d.id} className="border-t border-black/10" data-testid={`admin-row-${d.id}`}>
                  <td className="px-4 py-3 font-medium max-w-xs truncate">{d.title}</td>
                  <td className="px-4 py-3 uppercase text-xs tracking-widest">{d.category}</td>
                  <td className="px-4 py-3">{d.merchant}</td>
                  <td className="px-4 py-3 text-[#E63946] font-bold">{d.discount}</td>
                  <td className="px-4 py-3">{d.featured ? "✓" : "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => edit(d)} className="p-1 hover:text-[#E63946] mr-2" data-testid={`admin-edit-${d.id}`}><Edit3 className="w-4 h-4" strokeWidth={1.5} /></button>
                    <button onClick={() => remove(d.id)} className="p-1 hover:text-[#E63946]" data-testid={`admin-delete-${d.id}`}><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Newsletter panel */}
      <div className="mb-16" data-testid="newsletter-panel">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-serif font-bold text-2xl">Newsletter</h2>
          <button
            onClick={sendDaily}
            disabled={sendingDaily || !emailStatus?.configured || subs.length === 0}
            className="inline-flex items-center gap-2 bg-black text-white px-5 py-3 font-bold uppercase tracking-widest text-xs hover:bg-[#E63946] disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="send-daily-btn"
            title={!emailStatus?.configured ? "Add RESEND_API_KEY to backend/.env" : subs.length === 0 ? "No subscribers yet" : ""}
          >
            <Send className="w-4 h-4" strokeWidth={1.5} />
            {sendingDaily ? "Sending…" : "Send today's deal"}
          </button>
          <button
            onClick={sendWeekly}
            disabled={sendingWeekly || !emailStatus?.configured || subs.length === 0}
            className="inline-flex items-center gap-2 border border-black px-5 py-3 font-bold uppercase tracking-widest text-xs hover:bg-black hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="send-weekly-btn"
            title={!emailStatus?.configured ? "Add RESEND_API_KEY to backend/.env" : subs.length === 0 ? "No subscribers yet" : ""}
          >
            {sendingWeekly ? "Sending…" : "Send weekly recap"}
          </button>
        </div>

        <div className="border border-black/10 bg-white p-6 mb-4" data-testid="newsletter-status-panel">
          <div className="flex items-start gap-3">
            <Mail className="w-5 h-5 mt-0.5 shrink-0" strokeWidth={1.5} />
            <div className="text-sm flex-1">
              <div className="flex items-center gap-3 mb-1 flex-wrap">
                <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${emailStatus?.configured ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>
                  {emailStatus?.configured ? "Resend configured" : "Not configured"}
                </span>
                <span className="text-black/60">Sender: <span className="font-mono">{emailStatus?.sender || "—"}</span></span>
                <span className="text-black/60">Subscribers: <strong>{subs.length}</strong></span>
              </div>
              <div className="text-xs text-black/60 mb-1" data-testid="scheduler-info">
                <span className="font-mono uppercase tracking-widest text-[10px] font-bold text-[#E63946] mr-2">Daily</span>
                <span>{emailStatus?.schedule || "Daily at 08:00 UTC"}</span>
                {emailStatus?.next_scheduled_run && (
                  <> · Next: <span className="font-mono">{new Date(emailStatus.next_scheduled_run).toLocaleString()}</span></>
                )}
              </div>
              <div className="text-xs text-black/60 mb-2" data-testid="weekly-scheduler-info">
                <span className="font-mono uppercase tracking-widest text-[10px] font-bold text-[#E63946] mr-2">Weekly</span>
                <span>{emailStatus?.weekly_schedule || "Sundays at 09:00 UTC"}</span>
                {emailStatus?.next_weekly_run && (
                  <> · Next: <span className="font-mono">{new Date(emailStatus.next_weekly_run).toLocaleString()}</span></>
                )}
              </div>
              {!emailStatus?.configured && (
                <p className="text-black/60">
                  Add <code className="bg-black/5 px-1">RESEND_API_KEY</code> and <code className="bg-black/5 px-1">SENDER_EMAIL</code> to <code className="bg-black/5 px-1">backend/.env</code>, then restart the backend. Get a free key at <a href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer" className="underline hover:text-[#E63946]">resend.com/api-keys</a>. Featured deals are sent first — pick which one via the &ldquo;Featured&rdquo; checkbox on any deal.
                </p>
              )}
            </div>
          </div>
        </div>

        {emailLog.length > 0 && (
          <div className="border border-black/10 bg-white p-6 mb-4" data-testid="email-log">
            <div className="text-[10px] font-bold uppercase tracking-widest text-black/50 mb-3">Send history</div>
            <ul className="space-y-1 text-sm font-mono">
              {emailLog.map((l) => (
                <li key={l.id} className="flex items-center gap-3">
                  <span className="text-black/40 text-xs">{l.created_at?.slice(0, 16).replace("T", " ")}</span>
                  <span className="flex-1 truncate">{l.deal_title}</span>
                  <span className="text-emerald-600">✓ {l.sent}</span>
                  {l.failed > 0 && <span className="text-red-500">✗ {l.failed}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="border border-black/10 bg-white p-6">
          <div className="text-[10px] font-bold uppercase tracking-widest text-black/50 mb-3">Subscribers ({subs.length})</div>
          {subs.length === 0 ? (
            <p className="text-black/50 text-sm">No subscribers yet.</p>
          ) : (
            <ul className="space-y-1 text-sm font-mono max-h-64 overflow-y-auto">
              {subs.map((s) => <li key={s.email}>{s.email} <span className="text-black/40 text-xs">· {s.created_at?.slice(0, 10)}</span></li>)}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
